import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Collapse, ListItemButton, ListItemText } from '@mui/material'
import { DigitalTwin, useDigitalTwinVersions } from 'api/digitalTwin/digitalTwin.api'
import React, { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import Badge from 'ui/atoms/Badge/Badge'
import styles from './DigitalTwinList.module.less'

type DigitalTwinListProps = {
  digitalTwin: DigitalTwin
  primaryDigitalTwinUid: number | undefined
  currentDigitalTwin?: DigitalTwin
  setCurrentDigitalTwin: (digitalTwin?: DigitalTwin) => void
}

const GITHUB_ORG = 'sigholm'
const GITHUB_REPO_DT = 'aurora-optservice-customer-plugins'
const GITHUB_URL_DT_COMMIT = `https://github.com/${GITHUB_ORG}/${GITHUB_REPO_DT}/commit`

export default function DigitalTwinList({
  digitalTwin,
  primaryDigitalTwinUid,
  setCurrentDigitalTwin,
  currentDigitalTwin,
}: DigitalTwinListProps): ReactElement {
  const { t } = useTranslation()
  const { data: versions, refetch: fetchVersions } = useDigitalTwinVersions(digitalTwin.uid, 30)

  const open = currentDigitalTwin?.id === digitalTwin.id
  const latestModalTitle = `${digitalTwin.id} ${digitalTwin.display_name}`

  useEffect(() => {
    fetchVersions()
  }, [fetchVersions, digitalTwin.layout]) // Multiple versions can share the same layout. So if one changes, we need to reload all.

  const collapsedListItems = (
    <Collapse in={open} timeout="auto" unmountOnExit>
      {versions?.map((version, i) => {
        const buildUser = version.model?.build_metadata?.build_user
        const gitSha = version.model?.build_metadata?.git_sha
        const gitShaShort = gitSha ? gitSha.substring(0, 7) : undefined
        const githubCommitUrl = gitSha ? `${GITHUB_URL_DT_COMMIT}/${gitSha}` : undefined

        const versionModelTitle = (
          <>
            {`${digitalTwin.id}.${version.version} ${version.display_name}`}
            {buildUser && <span style={{ marginLeft: 8, color: colors.grey5 }}>{`by ${buildUser}`}</span>}
            {gitSha && (
              <a
                href={githubCommitUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: 8, color: colors.info4, textDecoration: 'underline' }}
                onClick={(e) => e.stopPropagation()}
                tabIndex={-1}
                onFocus={(e) => e.stopPropagation()}
              >
                {gitShaShort}
              </a>
            )}
          </>
        )
        const selected = currentDigitalTwin?.uid === version.uid

        let className: string | undefined
        if (!selected) {
          className = i % 2 === 0 ? styles.DigitalTwinList_WhiteBackground : styles.DigitalTwinList_GreyBackground
        } else {
          className = styles.DigitalTwinList_Selected
        }

        return (
          <ListItemButton
            className={className}
            selected={selected}
            key={`digital_twin_model_version_${version.uid}`}
            onClick={() => {
              setCurrentDigitalTwin(version)
            }}
            divider
            dense
          >
            <ListItemText primary={versionModelTitle} />
            {primaryDigitalTwinUid === version.uid ? <Badge success title={t('Primary')} /> : <></>}
          </ListItemButton>
        )
      })}
    </Collapse>
  )

  return (
    <>
      <ListItemButton
        key={`digital_twin_model_${digitalTwin.uid}`}
        onClick={() => {
          setCurrentDigitalTwin(open ? undefined : digitalTwin)
        }}
        divider
      >
        <ListItemText primary={latestModalTitle} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      {collapsedListItems}
    </>
  )
}
